'use client';

import NextLink from 'next/link';
import { usePathname } from 'next/navigation';

import type { ElementType } from 'react';

import { ArrowUpRight } from '@carbon/icons-react';
import { HeaderMenu, HeaderMenuItem } from '@carbon/react';

import { platformName } from '@/components/AppHeader';

import { getLinkProps, getUrlType } from '@/utils/links';
import type { HeaderMenuItemBaseProps } from '@carbon/react/lib/components/UIShell/HeaderMenuItem';
import styles from './app-header.module.scss';

export function AppMenuItems() {
  const pathname = usePathname();

  return (
    <>
      <HeaderMenuItem as={NextLink} href="/">
        <span className={styles['app-header__platform-name']}>{platformName}</span>
      </HeaderMenuItem>
      <AppHeaderMenuItem as={NextLink} href="/hardware" isActive={isActive(pathname, '/hardware')}>
        Hardware
      </AppHeaderMenuItem>
      <AppHeaderMenuItem as={NextLink} href="/qiskit" isActive={isActive(pathname, '/qiskit')}>
        Qiskit
      </AppHeaderMenuItem>
      <AppHeaderMenuItem as={NextLink} href="/products" isActive={isActive(pathname, '/products')}>
        Product
      </AppHeaderMenuItem>
      <AppHeaderMenuItem as={NextLink} href="/research" isActive={isActive(pathname, '/research')}>
        Research
      </AppHeaderMenuItem>
      <AppHeaderMenuItem as={NextLink} href="/blog" isActive={isActive(pathname, '/blog')}>
        Blog
      </AppHeaderMenuItem>

      <HeaderMenu
        aria-label="Community"
        menuLinkName="Community"
        className={styles['app-header__submenu']}
      >
        <AppHeaderSubMenuItem
          as={NextLink}
          href="/ibm-quantum-network"
          isActive={isActive(pathname, '/ibm-quantum-network')}
        >
          IBM Quantum Network
        </AppHeaderSubMenuItem>
        <AppHeaderSubMenuItem as={NextLink} href="/events" isActive={isActive(pathname, '/events')}>
          Events
        </AppHeaderSubMenuItem>
        <AppHeaderSubMenuItem
          as={NextLink}
          href="/ecosystem"
          isActive={isActive(pathname, '/ecosystem')}
        >
          Qiskit ecosystem
        </AppHeaderSubMenuItem>
      </HeaderMenu>

      <HeaderMenu
        aria-label="Resources"
        menuLinkName="Resources"
        className={styles['app-header__submenu']}
      >
        <AppHeaderSubMenuItem href="https://quantum.cloud.ibm.com/docs/">
          Documentation
        </AppHeaderSubMenuItem>
        <AppHeaderSubMenuItem href="https://quantum.cloud.ibm.com/learning">
          Learning
        </AppHeaderSubMenuItem>
        <AppHeaderSubMenuItem
          as={NextLink}
          href="/case-studies"
          isActive={isActive(pathname, '/case-studies')}
        >
          Case studies
        </AppHeaderSubMenuItem>
        <AppHeaderSubMenuItem
          as={NextLink}
          href="/quantum-credits"
          isActive={isActive(pathname, '/quantum-credits')}
        >
          IBM Quantum credits
        </AppHeaderSubMenuItem>
        <AppHeaderSubMenuItem
          as={NextLink}
          href="/feedback-program"
          isActive={isActive(pathname, '/feedback-program')}
        >
          Feedback program
        </AppHeaderSubMenuItem>
      </HeaderMenu>
    </>
  );
}

function AppHeaderSubMenuItem(
  props: HeaderMenuItemBaseProps & {
    href?: string;
    target?: string;
    as?: ElementType;
    isActive?: boolean;
  }
) {
  const { href, target: customTarget, children, ...rest } = props;
  const { target: linkTarget, ...linkProps } = getLinkProps(href);
  const target = customTarget ?? linkTarget;
  const urlType = getUrlType(href);
  const isExternalUrl = urlType === 'corporate-external' || urlType === 'external';

  return (
    <HeaderMenuItem
      href={href}
      target={target}
      className={styles['app-header__submenu__item']}
      {...rest}
      {...linkProps}
    >
      <span>
        {children}
        {isExternalUrl && <ArrowUpRight className={styles['app-header__submenu__icon']} />}
      </span>
    </HeaderMenuItem>
  );
}

function AppHeaderMenuItem(
  props: HeaderMenuItemBaseProps & {
    href?: string;
    target?: string;
    as?: ElementType;
    isActive?: boolean;
  }
) {
  return <HeaderMenuItem className={styles['app-header__menu-item']} {...props} />;
}

function isActive(pathname: string | null, url?: string): boolean {
  return pathname === url?.replace(/^\/quantum(?=\/|$)/, '');
}
